/**
 * InstructorTemplateDialog
 *
 * Modal form for creating or editing an instructor template. The
 * template is the reusable record that fills in across every course
 * the instructor teaches: name, credentials, bio, portrait. Same
 * dialog handles both "new" (empty form) and "edit" (prefilled from
 * the existing record).
 *
 * Portrait is a plain URL field today; swapping it for a proper upload
 * through the existing SAS flow is a future enhancement.
 */

import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '../ui';
import { CourseAuthor, SaveCourseAuthorRequest } from '../../apis/api-client';

export interface InstructorTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pass an existing author to "edit" mode; omit / pass null for
   *  "add new". For the add case, `initialName` seeds the Name field
   *  with whatever the user typed in the picker before opening this. */
  author: CourseAuthor | null;
  initialName?: string;
  onSave: (patch: SaveCourseAuthorRequest & { name: string }) => void;
}

export default function InstructorTemplateDialog({
  open,
  onClose,
  author,
  initialName,
  onSave,
}: InstructorTemplateDialogProps) {
  const isEdit = !!author;
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState('');
  const [bio, setBio] = useState('');
  const [portraitUrl, setPortraitUrl] = useState('');
  const [quote, setQuote] = useState('');
  const [nameError, setNameError] = useState('');
  // Focal point for the circular crop — % from left / top of the
  // source image. Defaults to dead center but can be re-aimed by
  // clicking on the preview below.
  const [focalX, setFocalX] = useState<number>(50);
  const [focalY, setFocalY] = useState<number>(50);

  // Hydrate the form when the dialog opens or the target author changes.
  useEffect(() => {
    if (!open) return;
    setName(author?.name ?? initialName ?? '');
    setCredentials(author?.credentials ?? '');
    setBio(author?.bio ?? '');
    setPortraitUrl(author?.portraitUrl ?? '');
    setQuote(author?.quote ?? '');
    setFocalX(author?.portraitFocalX ?? 50);
    setFocalY(author?.portraitFocalY ?? 50);
    setNameError('');
  }, [open, author, initialName]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Name is required');
      return;
    }
    onSave({
      name: trimmed,
      credentials,
      bio,
      portraitUrl,
      portraitFocalX: focalX,
      portraitFocalY: focalY,
      quote,
    });
  };

  // Click handler for the focal-point picker. Translates the
  // clicked pixel into a % of the displayed image and stores it.
  const handleFocalClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setFocalX(Math.max(0, Math.min(100, Math.round(x))));
    setFocalY(Math.max(0, Math.min(100, Math.round(y))));
  };

  const objectPosition = `${focalX}% ${focalY}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit instructor template' : 'Add new instructor'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These details are saved to the instructor's reusable template
          and auto-fill every course they teach.
        </Typography>

        {/* Live portrait preview — sits above the URL field so admins
            see immediately whether the URL resolves to an image. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'grey.50',
          }}
        >
          <Avatar
            src={portraitUrl || undefined}
            imgProps={{ style: { objectPosition } }}
            sx={{ width: 56, height: 56, fontSize: '1.25rem' }}
          >
            {name ? name.charAt(0).toUpperCase() : '?'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {name || 'Instructor name'}
            </Typography>
            {credentials && (
              <Typography variant="caption" color="text.secondary">
                {credentials}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Credentials"
            value={credentials}
            onChange={(e) => setCredentials(e.target.value)}
            placeholder="e.g. DDS, RDH, MS"
            helperText='Short professional letters shown beside the name as a pill.'
            fullWidth
            size="small"
          />
          <TextField
            label="Biography"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="A paragraph or two on the instructor's background, focus areas, and expertise."
            multiline
            minRows={4}
            fullWidth
          />
          <TextField
            label="Portrait URL"
            value={portraitUrl}
            onChange={(e) => setPortraitUrl(e.target.value)}
            placeholder="https://…/headshot.jpg"
            helperText="Public image URL. A proper upload field will replace this when the backend supports it."
            fullWidth
            size="small"
          />
          <TextField
            label="Pull quote"
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="A short signature line attributed to the instructor."
            helperText='Optional. Renders as a styled callout at the bottom of the learner "About the Instructor" tab.'
            multiline
            minRows={2}
          />

          {/* Focal-point picker — only renders when there's an image to
              position. Click anywhere on the portrait to set the center
              of the circular crop; the live avatar previews on the right
              update in real time. */}
          {portraitUrl && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ display: 'block', color: 'primary.main', mb: 0.5 }}
                >
                  Crop position
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Click anywhere on the photo to set where the circular
                  avatar should center. Re-aim if the face is getting cut
                  off in the previews on the right.
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: '100%',
                    maxWidth: 320,
                  }}
                >
                  <Box
                    component="img"
                    src={portraitUrl}
                    alt="Portrait"
                    onClick={handleFocalClick}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      cursor: 'crosshair',
                    }}
                  />
                  {/* Focal-point indicator — cyan ring + crosshair. */}
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      left: `${focalX}%`,
                      top: `${focalY}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      border: '3px solid #fff',
                      boxShadow: '0 0 0 2px rgba(var(--brand-rgb), 0.9), 0 2px 6px rgba(0,0,0,0.35)',
                      bgcolor: 'rgba(var(--brand-rgb), 0.25)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  pt: 2.75,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}
                >
                  Preview
                </Typography>
                <Avatar
                  src={portraitUrl}
                  imgProps={{ style: { objectPosition } }}
                  sx={{ width: 96, height: 96 }}
                />
                <Avatar
                  src={portraitUrl}
                  imgProps={{ style: { objectPosition } }}
                  sx={{ width: 56, height: 56 }}
                />
                <Avatar
                  src={portraitUrl}
                  imgProps={{ style: { objectPosition } }}
                  sx={{ width: 36, height: 36 }}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setFocalX(50);
                    setFocalY(50);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    minWidth: 0,
                  }}
                >
                  Reset to center
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ fontWeight: 700 }}
        >
          {isEdit ? 'Save changes' : 'Add instructor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
